/**
 * Interface i18n — powered by react-i18next with lazy locale loading.
 * Locale bundles are fetched on demand via loadLocale() and registered with
 * i18next.addResourceBundle(). Only the user's current language is loaded.
 */
import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { I18nextProvider, useTranslation as useI18nextTranslation } from "react-i18next";
import i18n, { STORAGE_KEY } from "./i18nextInit";
import { supportedLangs, defaultLang, loadLocale } from "./loadTranslations";

const I18nContext = createContext(null);

function getLangFromUrl() {
  if (typeof window === "undefined") return null;
  const seg = window.location.pathname.split("/")[1];
  return supportedLangs.includes(seg) ? seg : null;
}

/** Ensure the given language is fetched and registered in i18next. */
async function ensureLocaleLoaded(lang) {
  if (!supportedLangs.includes(lang)) return;
  const existing = i18n.getResourceBundle(lang, "translation");
  if (existing && Object.keys(existing).length > 0) return;
  try {
    const bundle = await loadLocale(lang);
    i18n.addResourceBundle(lang, "translation", bundle, true, true);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[i18n] Failed to load locale ${lang}:`, err?.message || err);
  }
}

/** Syncs <html lang/dir> with i18next */
function HtmlLangSync() {
  const { i18n: inst } = useI18nextTranslation();
  const lang = (inst.language || defaultLang).slice(0, 2);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "he" ? "rtl" : "ltr";
  }, [lang]);
  return null;
}

function I18nContextBridge({ children }) {
  const { t: i18nT, i18n: inst } = useI18nextTranslation();

  const lang = (inst.language || defaultLang).slice(0, 2);

  const setLang = useCallback(
    async (newLang) => {
      if (!supportedLangs.includes(newLang)) return;
      // Fetch the target locale bundle BEFORE switching so content doesn't flash
      await ensureLocaleLoaded(newLang);
      void inst.changeLanguage(newLang);
      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY, newLang);
      }
    },
    [inst]
  );

  const t = useCallback(
    (key, params) => {
      if (params && typeof params === "object") {
        return i18nT(key, params);
      }
      return i18nT(key);
    },
    [i18nT]
  );

  return (
    <I18nContext.Provider value={{ t, lang, setLang, supportedLangs }}>{children}</I18nContext.Provider>
  );
}

export function I18nProvider({ children, initialLang }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const fromUrl = getLangFromUrl();
    const stored =
      typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    const target =
      (initialLang && supportedLangs.includes(initialLang) && initialLang) ||
      fromUrl ||
      (stored && supportedLangs.includes(stored) ? stored : null) ||
      defaultLang;

    let cancelled = false;
    (async () => {
      await ensureLocaleLoaded(target);
      if (cancelled) return;
      if (i18n.language?.slice(0, 2) !== target) {
        await i18n.changeLanguage(target);
      }
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [initialLang]);

  // Wait for the first locale chunk before rendering children so we don't
  // flash translation keys instead of real strings.
  if (!ready) return null;

  return (
    <I18nextProvider i18n={i18n}>
      <HtmlLangSync />
      <I18nContextBridge>{children}</I18nContextBridge>
    </I18nextProvider>
  );
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useTranslation must be used within I18nProvider");
  return ctx;
}
